import { useNavigate, useSearch } from "@tanstack/react-router";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { setupAGGrid } from "@/lib/AGGrid/setupAGGrid";
import { ReportRelocationsTable } from "@/app/Reports/Reports/Components/ReportRelocations/ReportRelocationsTable";
import { ReportRelocationListItem } from "@/app/Reports/Reports/Components/ReportRelocations/reportRelocationsQuery";
import { Suspense, useCallback, useRef } from "react";
import { Button } from "@/components/catalyst/button";
import { DateRangePicker } from "@/components/ui/date-range/date-range-picker";
import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

setupAGGrid();

export function ReportRelocationScreen() {
  const gridRef = useRef<AgGridReact<ReportRelocationListItem>>(null);

  const { startDate, endDate } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/relocations",
  });

  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/relocations",
  });

  const { supplier } = useGlobalSupplier();

  const onBtExport = useCallback(
    (type: "csv" | "xls") => () => {
      const supplierPart = supplier ? `${supplier.name} ` : "";
      const fileName = `Imoova ${supplierPart}Relocations ${dayjs(
        startDate,
      ).format("DD-MM-YYYY")} to ${dayjs(endDate).format("DD-MM-YYYY")}`;

      if (type === "csv") {
        gridRef.current?.api.exportDataAsCsv({ fileName });
      } else {
        gridRef.current?.api.exportDataAsExcel({ fileName });
      }
    },
    [startDate, endDate, supplier],
  );

  return (
    <div className="flex h-full flex-col space-y-4 p-8">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/reports"
              params={(prev) => prev}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Relocations</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">
            Relocation Reports
          </h2>
          <p className="text-sm text-gray-500">
            View and export relocations created within the selected date range.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <DateRangePicker
            initialDateFrom={dayjs(startDate).toDate()}
            initialDateTo={dayjs(endDate).toDate()}
            onUpdate={({ range }) => {
              navigate({
                params: (prev) => prev,
                search: {
                  startDate: dayjs(range.from).format("YYYY-MM-DD"),
                  endDate: dayjs(range.to).format("YYYY-MM-DD"),
                },
              });
            }}
          />
          <Button onClick={onBtExport("csv")}>Export CSV</Button>
          <Button onClick={onBtExport("xls")}>Export excel</Button>
        </div>
      </div>

      <div className="grow rounded-lg">
        <Suspense fallback={<Skeleton className="mt-3 h-80" />}>
          <ReportRelocationsTable ref={gridRef} />
        </Suspense>
      </div>
    </div>
  );
}
